import { Select, TextInput } from '@mantine/core';
import { useMemo, useState } from 'react';

import { parsePhoneNumberFromString } from 'libphonenumber-js'; // optional but robust
import { toGroupedOptions } from '../../../utils/enumUtils';
import { useForm } from '@mantine/form';

/* ────────────────────────────────────────────────────────── */
/*  List of country calling codes – extend as you like       */
const COUNTRY_CODES = [
    /* ─────────── North America ─────────── */
    { label: '🇺🇸  +1', value: '+1', group: 'North America' },
    { label: '🇨🇦  +1', value: '+1-CA', group: 'North America' },
    { label: '🇲🇽  +52', value: '+52', group: 'North America' },

    /* ───── South & Central America ───── */
    { label: '🇧🇷  +55', value: '+55', group: 'South & Central America' },
    { label: '🇦🇷  +54', value: '+54', group: 'South & Central America' },
    { label: '🇨🇴  +57', value: '+57', group: 'South & Central America' },
    { label: '🇨🇱  +56', value: '+56', group: 'South & Central America' },
    { label: '🇵🇪  +51', value: '+51', group: 'South & Central America' },

    /* ───────────── Europe ───────────── */
    { label: '🇬🇧  +44', value: '+44', group: 'Europe' },
    { label: '🇩🇪  +49', value: '+49', group: 'Europe' },
    { label: '🇫🇷  +33', value: '+33', group: 'Europe' },
    { label: '🇮🇹  +39', value: '+39', group: 'Europe' },
    { label: '🇪🇸  +34', value: '+34', group: 'Europe' },
    { label: '🇳🇱  +31', value: '+31', group: 'Europe' },
    { label: '🇧🇪  +32', value: '+32', group: 'Europe' },
    { label: '🇸🇪  +46', value: '+46', group: 'Europe' },
    { label: '🇳🇴  +47', value: '+47', group: 'Europe' },
    { label: '🇩🇰  +45', value: '+45', group: 'Europe' },
    { label: '🇫🇮  +358', value: '+358', group: 'Europe' },
    { label: '🇵🇱  +48', value: '+48', group: 'Europe' },
    { label: '🇦🇹  +43', value: '+43', group: 'Europe' },
    { label: '🇨🇭  +41', value: '+41', group: 'Europe' },
    { label: '🇮🇪  +353', value: '+353', group: 'Europe' },
    { label: '🇵🇹  +351', value: '+351', group: 'Europe' },
    { label: '🇬🇷  +30', value: '+30', group: 'Europe' },
    { label: '🇨🇿  +420', value: '+420', group: 'Europe' },
    { label: '🇭🇺  +36', value: '+36', group: 'Europe' },
    { label: '🇺🇦  +380', value: '+380', group: 'Europe' },
    { label: '🇷🇺  +7', value: '+7', group: 'Europe' },

    /* ─────────── Middle East ─────────── */
    { label: '🇸🇦  +966', value: '+966', group: 'Middle East' },
    { label: '🇦🇪  +971', value: '+971', group: 'Middle East' },
    { label: '🇶🇦  +974', value: '+974', group: 'Middle East' },
    { label: '🇮🇱  +972', value: '+972', group: 'Middle East' },
    { label: '🇹🇷  +90', value: '+90', group: 'Middle East' },
    { label: '🇮🇷  +98', value: '+98', group: 'Middle East' },

    /* ───────────── Africa ───────────── */
    { label: '🇳🇬  +234', value: '+234', group: 'Africa' },
    { label: '🇪🇬  +20', value: '+20', group: 'Africa' },
    { label: '🇿🇦  +27', value: '+27', group: 'Africa' },
    { label: '🇰🇪  +254', value: '+254', group: 'Africa' },
    { label: '🇪🇹  +251', value: '+251', group: 'Africa' },
    { label: '🇬🇭  +233', value: '+233', group: 'Africa' },
    { label: '🇲🇦  +212', value: '+212', group: 'Africa' },

    /* ───────── Asia-Pacific ───────── */
    { label: '🇨🇳  +86', value: '+86', group: 'Asia-Pacific' },
    { label: '🇯🇵  +81', value: '+81', group: 'Asia-Pacific' },
    { label: '🇰🇷  +82', value: '+82', group: 'Asia-Pacific' },
    { label: '🇮🇳  +91', value: '+91', group: 'Asia-Pacific' },
    { label: '🇵🇰  +92', value: '+92', group: 'Asia-Pacific' },
    { label: '🇧🇩  +880', value: '+880', group: 'Asia-Pacific' },
    { label: '🇮🇩  +62', value: '+62', group: 'Asia-Pacific' },
    { label: '🇵🇭  +63', value: '+63', group: 'Asia-Pacific' },
    { label: '🇻🇳  +84', value: '+84', group: 'Asia-Pacific' },
    { label: '🇹🇭  +66', value: '+66', group: 'Asia-Pacific' },
    { label: '🇲🇾  +60', value: '+60', group: 'Asia-Pacific' },
    { label: '🇸🇬  +65', value: '+65', group: 'Asia-Pacific' },
    { label: '🇳🇿  +64', value: '+64', group: 'Asia-Pacific' },
    { label: '🇦🇺  +61', value: '+61', group: 'Asia-Pacific' },

    /* ────────── Central Asia ────────── */
    { label: '🇰🇿  +7-KZ', value: '+7-KZ', group: 'Central Asia' },
    { label: '🇺🇿  +998', value: '+998', group: 'Central Asia' },

    /* ─────────── Other / HK & TW ─────────── */
    { label: '🇭🇰  +852', value: '+852', group: 'Other Regions' },
    { label: '🇹🇼  +886', value: '+886', group: 'Other Regions' },
];

/* ────────────────────────────────────────────────────────── */
/*  Mantine-form validator for the phone number              */
/*  ─ Uses libphonenumber-js when available; falls back to   */
/*    a simple E.164-length check.                           */
export function phoneValidator(value, values) {
    if (!value) {
        return 'Please enter a valid phone number';
    }
    // remove all non-digit chars:
    const digits = value.replace(/\D/g, '');
    const countryCode = values.countryCode || '+1';
    const raw = countryCode.replace(/[^\d]/g, '') + digits;

    // ❶ Robust path (libphonenumber-js)
    try {
        const number = parsePhoneNumberFromString('+' + raw);
        return number && number.isValid()
            ? null
            : 'Please enter a valid phone number';
    } catch {
        return 'Please enter a valid phone number';
        /* ignore and fall through */
    }

    // ❷ Fallback: 8–15 digits after "+" (E.164 max 15)
    return /^\d{8,15}$/.test(raw) ? null : 'Please enter a valid phone number';
}

/* ────────────────────────────────────────────────────────── */
/*  Reusable component                                       */
export function PhoneNumberInput({
    form,
    name = 'phoneNumber', // path in form.values
    countryField = 'countryCode', // path for country code in form.values
    label = 'Phone number',
    placeholder = '(415) 420-6969',
    ...others
}) {
    // keep Select controlled by form too:
    const selectProps = form.getInputProps(countryField);

    /* Left-section element: a narrow <Select> */
    const leftSection = (
        <Select
            data={toGroupedOptions(COUNTRY_CODES)}
            searchable
            size='xs'
            w={75}
            withinPortal
            withCheckIcon={false}
            {...selectProps}
            // Since Select is inside an absolutely-positioned section,
            // stop clicks from bubbling to TextInput:
            leftSectionPointerEvents='all'
        />
    );

    return (
        <TextInput
            label={label}
            placeholder={placeholder}
            leftSection={leftSection}
            leftSectionWidth={80}
            // spread TextInput props from Mantine form:
            {...form.getInputProps(name)}
            {...others}
        />
    );
}
